// The super-admin org editor's Clyde tab — where FreshOrder sets an org's Clyde
// settings (Jon, 2026-10-21: agent settings are not for tenant admins yet; their
// PATCH /agent/settings answers 403). Reads and saves
// /api/super-admin/organizations/:id/agent-settings; the server's one writer
// (saveAgentSettings) audits every change. A save sends only the fields changed since
// the tab loaded, so an exemplar Clyde harvested meanwhile is never dropped by a save
// that did not touch the list.
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CowboyHatIcon } from "@/components/cowboy-hat-icon";
import { CLYDE_MODEL_IDS } from "@shared/clydeSettings";
import type { VoiceExemplarEntry } from "@shared/schema";

export type SuperAdminRequest = (url: string, options?: RequestInit) => Promise<any>;

interface ClydeSettings {
  agentEnabled: boolean;
  agentName: string | null;
  modelId: string;
  oversightMode: string;
  supervisorPhone: string | null;
  supervisorName: string | null;
  styleCard: string | null;
  voiceExemplars: VoiceExemplarEntry[];
  clydeAutoPlaceAfter: number | null;
  isDemo: boolean;
}

/** The form's own shape: every field as the input holds it. */
type FormState = {
  agentEnabled: boolean;
  agentName: string;
  modelId: string;
  oversightMode: string;
  supervisorPhone: string;
  supervisorName: string;
  styleCard: string;
  exemplars: string; // one per line
  autoPlace: string; // "off" | "2" | "3" | "5"
};

const toForm = (s: ClydeSettings): FormState => ({
  agentEnabled: s.agentEnabled,
  agentName: s.agentName ?? "",
  modelId: s.modelId,
  oversightMode: s.oversightMode,
  supervisorPhone: s.supervisorPhone ?? "",
  supervisorName: s.supervisorName ?? "",
  styleCard: s.styleCard ?? "",
  exemplars: s.voiceExemplars.map((e) => e.text).join("\n"),
  autoPlace: s.clydeAutoPlaceAfter == null ? "off" : String(s.clydeAutoPlaceAfter),
});

const exemplarLines = (text: string) => text.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);

/** The PATCH body: only what changed since the form loaded. */
export function changedClydeFields(loaded: FormState, now: FormState): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (now.agentEnabled !== loaded.agentEnabled) body.agentEnabled = now.agentEnabled;
  if (now.agentName !== loaded.agentName) body.agentName = now.agentName;
  if (now.modelId !== loaded.modelId) body.modelId = now.modelId;
  if (now.oversightMode !== loaded.oversightMode) body.oversightMode = now.oversightMode;
  if (now.supervisorPhone !== loaded.supervisorPhone) body.supervisorPhone = now.supervisorPhone;
  if (now.supervisorName !== loaded.supervisorName) body.supervisorName = now.supervisorName;
  if (now.styleCard !== loaded.styleCard) body.styleCard = now.styleCard;
  if (exemplarLines(now.exemplars).join("\n") !== exemplarLines(loaded.exemplars).join("\n")) {
    body.voiceExemplars = exemplarLines(now.exemplars);
  }
  if (now.autoPlace !== loaded.autoPlace) body.clydeAutoPlaceAfter = now.autoPlace === "off" ? null : Number(now.autoPlace);
  return body;
}

export function SuperAdminClydeSettings({ orgId, orgName, request }: { orgId: number; orgName: string; request: SuperAdminRequest }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const url = `/api/super-admin/organizations/${orgId}/agent-settings`;
  const queryKey = ["/api/super-admin/organizations", orgId, "agent-settings"];

  const { data, isLoading, error } = useQuery<ClydeSettings>({ queryKey, queryFn: () => request(url) });

  const [form, setForm] = useState<FormState | null>(null);
  const loadedRef = useRef<FormState | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const f = toForm(data);
    loadedRef.current = f;
    setForm(f);
  }, [data]);
  useEffect(() => setWarning(null), [orgId]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => (f ? { ...f, [key]: value } : f));

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      request(url, { method: "PATCH", body: JSON.stringify(body) }) as Promise<ClydeSettings & { warning?: string; changed: string[] }>,
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKey, saved);
      setWarning(saved.warning ?? null);
      toast({ description: saved.warning || (saved.changed.length ? "Clyde settings saved." : "Nothing changed.") });
    },
    onError: (e: unknown) => toast({ variant: "destructive", description: e instanceof Error ? e.message : "Failed to save." }),
  });

  if (isLoading || (!form && !error)) return <p className="text-sm text-muted-foreground">Loading Clyde settings…</p>;
  if (error || !data || !form || !loadedRef.current) {
    return <p className="text-sm text-red-600">Couldn't load Clyde settings{error instanceof Error ? `: ${error.message}` : "."}</p>;
  }

  const loaded = loadedRef.current;
  const body = changedClydeFields(loaded, form);
  const dirty = Object.keys(body).length > 0;
  const modelChoices = (CLYDE_MODEL_IDS as readonly string[]).includes(data.modelId) ? [...CLYDE_MODEL_IDS] : [data.modelId, ...CLYDE_MODEL_IDS];
  const harvestedCount = data.voiceExemplars.filter((e) => e.source !== "seed").length;

  const onSave = () => {
    if (body.agentEnabled === true && !window.confirm(`Switch Clyde on for ${orgName}? Clyde starts answering texts to this organization's number.`)) return;
    if (body.agentEnabled === false && !window.confirm(`Switch Clyde off for ${orgName}? Texts to its number stop reaching Clyde.`)) return;
    save.mutate(body);
  };

  return (
    <div className="space-y-6" data-testid="super-admin-clyde-settings">
      <div className="flex items-start gap-3">
        <CowboyHatIcon className="h-6 w-6 text-violet-600 mt-0.5" />
        <div>
          <h3 className="text-base font-medium">Clyde</h3>
          <p className="text-sm text-muted-foreground">
            Set by FreshOrder only. The vendor's admins see the conversations, never these settings. Every change is audited.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <Label htmlFor="clyde-enabled" className="text-base">Clyde on</Label>
          <p className="text-sm text-muted-foreground">
            {data.isDemo
              ? "This is a demo organization — Clyde is never switched on for one."
              : "On: Clyde answers texts to this organization's number, and the vendor sees the Clyde tab."}
          </p>
        </div>
        <Switch
          id="clyde-enabled"
          checked={form.agentEnabled}
          onCheckedChange={(v) => set("agentEnabled", v)}
          disabled={data.isDemo && !form.agentEnabled}
          data-testid="switch-clyde-enabled"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="clyde-agent-name">Name buyers see</Label>
          <Input id="clyde-agent-name" value={form.agentName} onChange={(e) => set("agentName", e.target.value)} placeholder="Clyde" maxLength={100} data-testid="input-clyde-agent-name" />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="clyde-model">Model</Label>
          <Select value={form.modelId} onValueChange={(v) => set("modelId", v)}>
            <SelectTrigger id="clyde-model" data-testid="select-clyde-model"><SelectValue /></SelectTrigger>
            <SelectContent>
              {modelChoices.map((m) => (
                <SelectItem key={m} value={m}>{m}{(CLYDE_MODEL_IDS as readonly string[]).includes(m) ? "" : " (stored, no longer offered)"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="clyde-supervisor-phone">Supervisor phone</Label>
          <Input id="clyde-supervisor-phone" value={form.supervisorPhone} onChange={(e) => set("supervisorPhone", e.target.value)} placeholder="+1 519 555 0123" data-testid="input-clyde-supervisor-phone" />
          <p className="text-xs text-muted-foreground">Where Clyde texts a question it can't answer, and the rep the Start-a-thread dialog suggests. Blank turns off text escalations.</p>
          {warning && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="clyde-settings-warning">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
              <span>{warning}</span>
            </div>
          )}
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="clyde-supervisor-name">Supervisor name</Label>
          <Input id="clyde-supervisor-name" value={form.supervisorName} onChange={(e) => set("supervisorName", e.target.value)} placeholder="Kyle" maxLength={100} data-testid="input-clyde-supervisor-name" />
          <p className="text-xs text-muted-foreground">What buyers call the rep. The console sends as this name on a thread with no rep of its own.</p>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="clyde-auto-place">Skip the recap for trusted buyers</Label>
          <Select value={form.autoPlace} onValueChange={(v) => set("autoPlace", v)}>
            <SelectTrigger id="clyde-auto-place" data-testid="select-clyde-auto-place"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off — always recap and wait for YES</SelectItem>
              <SelectItem value="2">After 2 orders confirmed as recapped</SelectItem>
              <SelectItem value="3">After 3 orders confirmed as recapped</SelectItem>
              <SelectItem value="5">After 5 orders confirmed as recapped</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">The trust ladder (group-thread-transport.md §9). Off at launch.</p>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="clyde-oversight">Oversight mode</Label>
          <Select value={form.oversightMode} onValueChange={(v) => set("oversightMode", v)}>
            <SelectTrigger id="clyde-oversight" data-testid="select-clyde-oversight"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="review_all">review_all</SelectItem>
              <SelectItem value="auto_confirm">auto_confirm</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Stored only — nothing in Clyde reads it today.</p>
        </div>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="clyde-style-card">Voice — style card</Label>
        <Textarea id="clyde-style-card" value={form.styleCard} onChange={(e) => set("styleCard", e.target.value)} rows={3} maxLength={2000} data-testid="input-clyde-style-card" />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="clyde-exemplars">Voice — example texts (one per line, up to 15)</Label>
        <Textarea id="clyde-exemplars" value={form.exemplars} onChange={(e) => set("exemplars", e.target.value)} rows={5} data-testid="input-clyde-exemplars" />
        <p className="text-xs text-muted-foreground">
          Tone reference only, never repeated to buyers.
          {harvestedCount > 0 && ` ${harvestedCount} picked up automatically from the rep's take-overs and text answers.`}
        </p>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={!dirty || save.isPending} data-testid="btn-save-clyde-settings">
          <Save className="h-4 w-4 mr-2" />
          {save.isPending ? "Saving…" : "Save Clyde settings"}
        </Button>
      </div>
    </div>
  );
}
